import {
  normalizeSelectOptions,
  resolveOptionByValue,
  type SelectOption,
} from '@/lib/databases/values';
import type { DatabaseColumnDefinition } from './types';
import type { BlockRecord } from '../blocks/types';
import type { DatabaseQueryGroup } from './types';

export type BoardLane = {
  key: string | null;
  label: string;
  color: string | null;
  rows: BlockRecord[];
};

const EMPTY_LANE_KEY = '__empty__';

export function getSelectOptions(column: DatabaseColumnDefinition | undefined): SelectOption[] {
  if (!column || column.type !== 'select') {
    return [];
  }
  return normalizeSelectOptions(column.config);
}

/** @deprecated Use getSelectOptions */
export function getSelectOptionLabels(column: DatabaseColumnDefinition | undefined): string[] {
  return getSelectOptions(column).map((o) => o.name);
}

export function laneLabelForKey(
  key: string | null,
  column: DatabaseColumnDefinition | undefined,
): string {
  if (normalizeBoardLaneKey(key) === null) {
    return 'Empty';
  }
  const opt = resolveOptionByValue(column?.config, key);
  return opt?.name ?? key!;
}

export function laneColorForKey(
  key: string | null,
  column: DatabaseColumnDefinition | undefined,
): string | null {
  if (normalizeBoardLaneKey(key) === null) {
    return null;
  }
  return resolveOptionByValue(column?.config, key)?.color ?? null;
}

/** Board/API may use null or '' for an unset select value; treat both as empty. */
export function normalizeBoardLaneKey(key: string | null | undefined): string | null {
  if (key === null || key === undefined || key === '') {
    return null;
  }
  return key;
}

export function laneLabel(key: string | null): string {
  if (normalizeBoardLaneKey(key) === null) {
    return 'Empty';
  }
  return key!;
}

export function laneKeyForDrop(key: string | null): string | null {
  if (key === EMPTY_LANE_KEY) {
    return null;
  }
  return key;
}

export function buildBoardLanes(
  groups: DatabaseQueryGroup[],
  column: DatabaseColumnDefinition | undefined,
): BoardLane[] {
  const options = getSelectOptions(column);
  const rowsByKey = new Map<string | null, BlockRecord[]>();
  for (const group of groups) {
    const key = normalizeBoardLaneKey(group.key);
    const prev = rowsByKey.get(key) ?? [];
    rowsByKey.set(key, [...prev, ...group.rows]);
  }

  const lanes: BoardLane[] = [
    {
      key: null,
      label: laneLabel(null),
      color: null,
      rows: rowsByKey.get(null) ?? [],
    },
  ];
  rowsByKey.delete(null);

  const usedKeys = new Set<string | null>([null]);

  for (const opt of options) {
    lanes.push({
      key: opt.id,
      label: opt.name,
      color: opt.color,
      rows: rowsByKey.get(opt.id) ?? rowsByKey.get(opt.name) ?? [],
    });
    rowsByKey.delete(opt.id);
    rowsByKey.delete(opt.name);
    usedKeys.add(opt.id);
  }

  for (const [key, rows] of rowsByKey) {
    if (key === null || key === '') {
      continue;
    }
    const resolved = resolveOptionByValue(column?.config, key);
    lanes.push({
      key: resolved?.id ?? key,
      label: resolved?.name ?? laneLabel(key),
      color: resolved?.color ?? null,
      rows,
    });
  }

  return lanes;
}

/** Value stored in block_properties for a select cell after drop. */
export function boardDropPropertyValue(laneKey: string | null): Record<string, unknown> {
  const v = laneKeyForDrop(laneKey);
  if (v === null) {
    return { t: 'select', v: '' };
  }
  return { t: 'select', v };
}

export function boardLaneDropKey(lane: BoardLane): string {
  return lane.key === null ? EMPTY_LANE_KEY : lane.key;
}
